import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, ArrowRight, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios, { API } from '@/utils/api';

const UpgradeSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Verifying your payment...');
  const attemptsRef = useRef(0);
  const maxAttempts = 5;

  // SECURITY: Auth via httpOnly cookies
  // Note: API, axios are module-level constants; attemptsRef is a ref; maxAttempts is a constant
  const pollStatus = useCallback(async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage('Payment successful! Your account has been upgraded to Premium.');
        
        // Refresh user data
        try {
          const userRes = await axios.get(`${API}/auth/me`);
          console.log('User upgraded to:', userRes.data.membership_tier);
        } catch (e) {
          console.debug('Could not refresh user data');
        }
        return;
      }

      if (response.data.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
        return;
      }

      // Continue polling if not yet confirmed
      if (attemptsRef.current < maxAttempts) {
        attemptsRef.current += 1;
        setTimeout(() => pollStatus(sessionId), 2000);
      } else {
        setStatus('success');
        setMessage('Payment received! Your upgrade is being processed.');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attemptsRef.current < maxAttempts) {
        attemptsRef.current += 1;
        setTimeout(() => pollStatus(sessionId), 2000);
      } else {
        setStatus('error');
        setMessage('Could not verify payment. Please check your email for confirmation.');
      }
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      setMessage('No payment session found. Please try again.');
      return;
    }

    pollStatus(sessionId);
  }, [searchParams, pollStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-[#2D063E]/50 border border-[#df1283]/30 rounded-2xl p-8 text-center">
          {status === 'checking' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#df1283]/20 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#df1283] animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
              <p className="text-gray-400">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Welcome to Premium!</h1>
              <p className="text-gray-400 mb-6">{message}</p>
              
              <div className="bg-gradient-to-r from-[#df1283]/20 to-[#FF7901]/20 border border-[#df1283]/30 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-yellow-400" />
                  <span className="font-semibold text-white">Premium Features Unlocked</span>
                </div>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>✓ Unlimited journal entries</li>
                  <li>✓ Unlimited AI coaching</li>
                  <li>✓ Unlimited voice transcription</li>
                  <li>✓ Premium badges & rewards</li>
                  <li>✓ Priority support</li>
                </ul>
              </div>

              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                <span className="text-3xl">⚠️</span>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Something Went Wrong</h1>
              <p className="text-gray-400 mb-6">{message}</p>
              
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  Return to Dashboard
                </Button>
                <Button 
                  onClick={() => navigate('/upgrade')}
                  className="w-full bg-gradient-to-r from-[#df1283] to-[#FF7901]"
                >
                  Try Again
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpgradeSuccess;
